// Catalog Shell - Clipboard Utilities
// ============================================================================

import { createButton, type ButtonSize } from './pane-ui';
import { icons } from './icons';

// ============================================================================
// Low-level clipboard API
// ============================================================================

/**
 * Copy text to the clipboard. Returns true on success, false on failure.
 * Uses the Clipboard API with a fallback for older browsers / non-secure contexts.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        // Fallback for older browsers or non-secure contexts
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        textarea.style.pointerEvents = 'none';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
            return true;
        } catch {
            return false;
        } finally {
            textarea.remove();
        }
    }
}

// ============================================================================
// Copy Button
// ============================================================================

export interface CopyButtonConfig {
    /** Function that returns the text to copy (called on click, not at creation) */
    getText: () => string;
    /** Button label. Default: 'Copy' */
    label?: string;
    /** Label shown after successful copy. Default: 'Copied!' */
    copiedLabel?: string;
    /** Duration to show the copied state in ms. Default: 2000 */
    feedbackDuration?: number;
    /** Button size. Default: 'small' */
    size?: ButtonSize;
    /** Additional CSS class */
    className?: string;
}

/**
 * Creates a button that copies text to the clipboard with visual feedback.
 * Shows a temporary "Copied!" state after a successful copy.
 */
export function createCopyButton(config: CopyButtonConfig): HTMLButtonElement {
    const {
        getText,
        label = 'Copy',
        copiedLabel = 'Copied!',
        feedbackDuration = 2000,
        size = 'small',
        className,
    } = config;

    let timeout: number | undefined;

    const btn = createButton({
        label,
        size,
        icon: icons.copy(),
        className: className ? `cs-copy-btn ${className}` : 'cs-copy-btn',
        onClick: async () => {
            const text = getText();
            const ok = await copyToClipboard(text);
            if (ok) {
                btn.classList.add('cs-copy-btn--copied');
                const iconSpan = btn.querySelector('.cs-btn-icon');
                if (iconSpan) {
                    iconSpan.innerHTML = '';
                    iconSpan.appendChild(icons.check());
                }
                const textNode = Array.from(btn.childNodes).find(
                    n => n.nodeType === Node.TEXT_NODE
                );
                if (textNode) {
                    textNode.textContent = copiedLabel;
                }
                clearTimeout(timeout);
                timeout = window.setTimeout(() => {
                    btn.classList.remove('cs-copy-btn--copied');
                    if (iconSpan) {
                        iconSpan.innerHTML = '';
                        iconSpan.appendChild(icons.copy());
                    }
                    if (textNode) {
                        textNode.textContent = label;
                    }
                }, feedbackDuration);
            }
        },
    });

    return btn;
}
